import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Mail, DollarSign, Save, AlertCircle, Send, PlayCircle, FileDown } from "lucide-react";

const TEST_EMAIL_TYPES = [
  { id: "welcome", label: "Welcome Email" },
  { id: "password_reset", label: "Password Reset" },
  { id: "carrier_alert", label: "Carrier Status Alert" },
  { id: "expiry_reminder", label: "Expiry Reminder" },
  { id: "weekly_digest", label: "Weekly Digest" },
];

export default function AdminSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stripeKey, setStripeKey] = useState("");
  const [stripePub, setStripePub] = useState("");
  const [stripeWh, setStripeWh] = useState("");
  const [resendKey, setResendKey] = useState("");
  const [resendFrom, setResendFrom] = useState("");
  const [resendReply, setResendReply] = useState("");
  const [supportAlertEmail, setSupportAlertEmail] = useState("");
  const [fmcsaKey, setFmcsaKey] = useState("");
  const [savingKeys, setSavingKeys] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [savingTiers, setSavingTiers] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");
  const [testingType, setTestingType] = useState("");
  const [watchdogResult, setWatchdogResult] = useState(null);
  const [runningWatchdog, setRunningWatchdog] = useState(false);
  const [digestAnnouncement, setDigestAnnouncement] = useState("");
  const [savingDigest, setSavingDigest] = useState(false);
  const [digestResult, setDigestResult] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings");
      setData(data);
      setStripeKey("");
      setStripePub("");
      setStripeWh("");
      setResendKey("");
      setResendFrom(data.resend_from_email || "");
      setResendReply(data.resend_reply_to || "");
      setSupportAlertEmail(data.support_alert_email || "");
      setDigestAnnouncement(data.digest_announcement || "");
      setFmcsaKey("");
      setTiers(data.pricing_tiers || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveKeys = async () => {
    setSavingKeys(true);
    try {
      const payload = {};
      if (stripeKey.trim()) payload.stripe_secret_key = stripeKey.trim();
      if (stripePub.trim()) payload.stripe_publishable_key = stripePub.trim();
      if (stripeWh.trim()) payload.stripe_webhook_secret = stripeWh.trim();
      if (resendKey.trim()) payload.resend_api_key = resendKey.trim();
      if (resendFrom !== (data?.resend_from_email || "")) payload.resend_from_email = resendFrom;
      if (resendReply !== (data?.resend_reply_to || "")) payload.resend_reply_to = resendReply;
      if (supportAlertEmail !== (data?.support_alert_email || "")) payload.support_alert_email = supportAlertEmail;
      if (fmcsaKey.trim()) payload.fmcsa_webkey = fmcsaKey.trim();
      if (Object.keys(payload).length === 0) { toast("Nothing to save"); return; }
      await api.put("/admin/settings", payload);
      toast.success("Credentials updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingKeys(false); }
  };

  const saveTiers = async () => {
    setSavingTiers(true);
    try {
      const pricing = {};
      for (const t of tiers) {
        pricing[t.id] = {
          name: t.name, amount: Number(t.amount), vehicles: Number(t.vehicles),
          features: (t.features || []).filter(Boolean),
        };
      }
      await api.put("/admin/settings", { pricing_tiers: pricing });
      toast.success("Pricing updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingTiers(false); }
  };

  const [downloadingOverview, setDownloadingOverview] = useState(false);

  const downloadOverview = async () => {
    setDownloadingOverview(true);
    try {
      const res = await api.get("/reports/platform-overview.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "crystal_compliance_platform_overview.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    } finally { setDownloadingOverview(false); }
  };

  const sendTestEmail = async (type) => {
    if (!testRecipient.trim()) { toast.error("Enter a recipient email first"); return; }
    setTestingType(type);
    try {
      await api.post("/admin/test-email", { email_type: type, recipient: testRecipient.trim() });
      toast.success(`Test "${TEST_EMAIL_TYPES.find((t) => t.id === type)?.label}" sent to ${testRecipient.trim()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test email failed");
    } finally { setTestingType(""); }
  };

  const runWatchdog = async () => {
    setRunningWatchdog(true);
    try {
      const { data } = await api.post("/admin/email-watchdog/run");
      setWatchdogResult(data);
      toast.success(`Watchdog complete — ${data.emails_sent} reminder email(s) sent`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Watchdog run failed");
    } finally { setRunningWatchdog(false); }
  };

  const saveDigestAnnouncement = async () => {
    setSavingDigest(true);
    try {
      await api.put("/admin/settings", { digest_announcement: digestAnnouncement });
      toast.success("Announcement saved — it will appear in every weekly digest");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingDigest(false); }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/weekly-digest/run");
      setDigestResult(data);
      toast.success(`Digest sent to ${data.digests_sent} of ${data.clients} client(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest run failed");
    } finally { setSendingDigest(false); }
  };

  const updateTier = (id, k, v) => setTiers(tiers.map((t) => t.id === id ? { ...t, [k]: v } : t));
  const updateFeature = (id, idx, v) => setTiers(tiers.map((t) => t.id === id ? { ...t, features: t.features.map((f, i) => i === idx ? v : f) } : t));
  const addFeature = (id) => setTiers(tiers.map((t) => t.id === id ? { ...t, features: [...(t.features || []), ""] } : t));
  const removeFeature = (id, idx) => setTiers(tiers.map((t) => t.id === id ? { ...t, features: t.features.filter((_, i) => i !== idx) } : t));

  if (loading || !data) return <div className="hs-overline">LOADING…</div>;

  return (
    <div className="space-y-8" data-testid="admin-settings-page">
      <div>
        <div className="hs-overline text-blue-700">SYSTEM · ADMIN</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-2">Settings</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">Configure email delivery, alerts, and general portal settings. Keys are stored securely in the database and take effect immediately.</p>
      </div>

      {/* Launch pack */}
      <div className="hs-card p-6 flex flex-wrap items-center justify-between gap-4" data-testid="settings-launch-pack-card">
        <div>
          <div className="flex items-center gap-2">
            <FileDown size={16} className="text-blue-700"/>
            <h3 className="font-display font-bold text-xl">Platform Overview (Launch PDF)</h3>
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            Downloadable launch documentation: full role &amp; permissions matrix, everything that runs automatically vs. what clients and your team handle manually, all {""}filings tracked, and live integration status.
          </p>
        </div>
        <button
          onClick={downloadOverview}
          disabled={downloadingOverview}
          className="hs-btn-primary inline-flex items-center gap-2"
          data-testid="settings-download-overview"
        >
          <FileDown size={14}/> {downloadingOverview ? "GENERATING…" : "DOWNLOAD PDF"}
        </button>
      </div>

      {/* Email */}
      <div className="hs-card p-6 space-y-6">
        <div>
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-blue-700"/>
            <h3 className="font-display font-bold text-xl">Email (Resend)</h3>
            <span className={`hs-tag ${data.resend_key_set ? "hs-tag-success" : "hs-tag-warn"}`}>
              {data.resend_source === "database" ? "DB · ACTIVE" : data.resend_source === "env" ? "ENV · ACTIVE" : "NOT SET"}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Current key: <code className="font-mono">{data.resend_key_masked || "(not set)"}</code>.
            Get a free key at <a className="text-blue-700 hover:underline" href="https://resend.com/api-keys" target="_blank" rel="noreferrer">resend.com</a>.
            To send from your own domain, verify it at <a className="text-blue-700 hover:underline" href="https://resend.com/domains" target="_blank" rel="noreferrer">resend.com/domains</a> first.
          </p>
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            <div>
              <label className="hs-overline">Resend API Key</label>
              <input
                type="password"
                className="hs-input mt-2 font-mono"
                placeholder={data.resend_key_set ? "•••••••• (leave blank to keep)" : "re_xxxxxxxxx"}
                value={resendKey}
                onChange={(e) => setResendKey(e.target.value)}
                data-testid="settings-resend-input"
              />
            </div>
            <div>
              <label className="hs-overline">From address</label>
              <input
                type="text"
                className="hs-input mt-2"
                placeholder="Crystal Compliance <alerts@yourdomain.com>"
                value={resendFrom}
                onChange={(e) => setResendFrom(e.target.value)}
                data-testid="settings-resend-from"
              />
            </div>
            <div>
              <label className="hs-overline">Reply-to</label>
              <input
                type="email"
                className="hs-input mt-2"
                placeholder="support@crystallogistics.services"
                value={resendReply}
                onChange={(e) => setResendReply(e.target.value)}
                data-testid="settings-resend-reply"
              />
            </div>
            <div>
              <label className="hs-overline">Support alert inbox</label>
              <input
                type="email"
                className="hs-input mt-2"
                placeholder="support@crystallogistics.services"
                value={supportAlertEmail}
                onChange={(e) => setSupportAlertEmail(e.target.value)}
                data-testid="settings-support-alert-email"
              />
              <p className="text-[10px] text-slate-500 mt-1">Client chat alerts go to this inbox. Leave blank to alert every admin account email instead.</p>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-6">
          <div className="flex items-center gap-2">
            <KeyRound size={16} className="text-blue-700"/>
            <h3 className="font-display font-bold text-xl">FMCSA SAFER (Carrier Safety Lookups)</h3>
            <span className={`hs-tag ${data.fmcsa_key_set ? "hs-tag-success" : "hs-tag-warn"}`}>
              {data.fmcsa_key_set ? "CONFIGURED" : "NOT SET"}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Current key: <code className="font-mono">{data.fmcsa_key_masked || "(not set)"}</code>.
            Get a free WebKey at <a className="text-blue-700 hover:underline" href="https://mobile.fmcsa.dot.gov/QCDevsite/docs/apiAccess" target="_blank" rel="noreferrer">mobile.fmcsa.dot.gov</a>. Unlimited carrier snapshots by DOT number.
          </p>
          <label className="hs-overline mt-4 block">FMCSA WebKey</label>
          <input
            type="password"
            className="hs-input mt-2 font-mono"
            placeholder={data.fmcsa_key_set ? "•••••••• (leave blank to keep)" : "your-webkey"}
            value={fmcsaKey}
            onChange={(e) => setFmcsaKey(e.target.value)}
            data-testid="settings-fmcsa-input"
          />
        </div>

        <div className="flex items-start gap-2 p-3 border border-blue-200 bg-blue-50 text-xs text-slate-700">
          <AlertCircle size={14} className="text-blue-700 flex-shrink-0 mt-0.5"/>
          <div>
            Both keys are encrypted at rest in the database. Once saved, Stripe checkout and email notifications activate immediately without redeploy. To rotate a key, simply save a new value.
          </div>
        </div>

        <button onClick={saveKeys} disabled={savingKeys} className="hs-btn-primary inline-flex items-center gap-2" data-testid="settings-save-keys">
          <Save size={14}/> {savingKeys ? "SAVING…" : "SAVE CREDENTIALS"}
        </button>
      </div>

      {/* Email testing */}
      <div className="hs-card p-6" data-testid="settings-email-testing-card">
        <div className="flex items-center gap-2 mb-1">
          <Send size={16} className="text-blue-700"/>
          <h3 className="font-display font-bold text-xl">Email Testing</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Send a sample of each transactional email to verify your Resend setup. Note: until your domain is verified in Resend, test emails only deliver to the email address on your Resend account.
        </p>
        <label className="hs-overline">Send test to</label>
        <input
          type="email"
          className="hs-input mt-2 max-w-md"
          placeholder="you@example.com"
          value={testRecipient}
          onChange={(e) => setTestRecipient(e.target.value)}
          data-testid="settings-test-email-recipient"
        />
        <div className="flex flex-wrap gap-2 mt-4">
          {TEST_EMAIL_TYPES.map((t) => (
            <button
              key={t.id}
              onClick={() => sendTestEmail(t.id)}
              disabled={!!testingType}
              className="hs-btn-ghost inline-flex items-center gap-2 border border-slate-300 px-3 py-2 text-xs"
              data-testid={`settings-test-email-${t.id}`}
            >
              <Send size={12}/> {testingType === t.id ? "SENDING…" : t.label.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="border-t border-slate-200 mt-6 pt-6">
          <div className="flex items-center gap-2">
            <PlayCircle size={16} className="text-blue-700"/>
            <h4 className="font-display font-bold text-base">Expiry Watchdog</h4>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Runs automatically once a day — scans every client&apos;s filings, CDLs, medical cards and drug-test dates, then emails reminders at 30 / 7 / 1 days before expiry. Run it manually to test.
          </p>
          <button
            onClick={runWatchdog}
            disabled={runningWatchdog}
            className="hs-btn-primary inline-flex items-center gap-2 mt-3"
            data-testid="settings-run-watchdog"
          >
            <PlayCircle size={14}/> {runningWatchdog ? "RUNNING…" : "RUN WATCHDOG NOW"}
          </button>
          {watchdogResult && (
            <div className="mt-3 p-3 border border-blue-200 bg-blue-50 text-xs font-mono" data-testid="settings-watchdog-result">
              Clients scanned: {watchdogResult.clients} · Items checked: {watchdogResult.items_checked} · Reminder emails sent: {watchdogResult.emails_sent}
            </div>
          )}
        </div>
      </div>

      {/* Weekly digest */}
      <div className="hs-card p-6" data-testid="settings-weekly-digest-card">
        <div className="flex items-center gap-2 mb-1">
          <Mail size={16} className="text-blue-700"/>
          <h3 className="font-display font-bold text-xl">Weekly Client Digest</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Every week, each client automatically receives a branded report with their safety score, expiring filings, driver credential alerts and document statuses. Add a custom &quot;What&apos;s New &amp; Coming Soon&quot; message below — one item per line — and it appears in every digest.
        </p>
        <label className="hs-overline">What&apos;s New &amp; Coming Soon (one item per line)</label>
        <textarea
          className="hs-input mt-2 min-h-[110px] resize-y"
          placeholder={"New: weekly compliance reports straight to your inbox\nComing soon: bulk driver & permit import\nTip: upload renewed documents as soon as you get them"}
          value={digestAnnouncement}
          onChange={(e) => setDigestAnnouncement(e.target.value)}
          data-testid="settings-digest-announcement"
        />
        <div className="flex flex-wrap gap-2 mt-4">
          <button
            onClick={saveDigestAnnouncement}
            disabled={savingDigest}
            className="hs-btn-primary inline-flex items-center gap-2"
            data-testid="settings-save-digest"
          >
            <Save size={14}/> {savingDigest ? "SAVING…" : "SAVE ANNOUNCEMENT"}
          </button>
          <button
            onClick={sendDigestNow}
            disabled={sendingDigest}
            className="hs-btn-ghost inline-flex items-center gap-2 border border-slate-300 px-3"
            data-testid="settings-send-digest-now"
          >
            <Send size={14}/> {sendingDigest ? "SENDING…" : "SEND DIGEST TO ALL CLIENTS NOW"}
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Preview it first: use the &quot;Weekly Digest&quot; button in the Email Testing card above to send a sample to yourself.
        </p>
        {digestResult && (
          <div className="mt-3 p-3 border border-blue-200 bg-blue-50 text-xs font-mono" data-testid="settings-digest-result">
            Clients: {digestResult.clients} · Digests delivered: {digestResult.digests_sent}
          </div>
        )}
      </div>

      <ResetDemoDataCard />

      <OperationsManualCard />
    </div>
  );
}

function OperationsManualCard() {
  const [downloading, setDownloading] = useState(false);
  const downloadManual = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/operations-manual.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `crystal_logistics_operations_manual.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Operations Manual downloaded");
    } catch (e) {
      toast.error("Failed to generate manual");
    } finally { setDownloading(false); }
  };
  return (
    <div className="hs-card p-6" data-testid="settings-operations-manual-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="hs-overline text-blue-700">TEAM TRAINING</div>
          <h2 className="font-display font-bold text-xl mt-1">Operations Manual (PDF)</h2>
          <p className="text-xs text-slate-500 mt-2 max-w-2xl">
            17-section internal manual covering user roles, daily / monthly workflows, client onboarding,
            CSA &amp; Safety modules, email alerts, support chat, sales talking points, troubleshooting,
            and roadmap. Give this to every new trainee on the team.
          </p>
        </div>
        <button
          onClick={downloadManual}
          disabled={downloading}
          className="hs-btn-primary inline-flex items-center gap-2"
          data-testid="settings-download-manual-btn"
        >
          <Save size={14} /> {downloading ? "GENERATING…" : "DOWNLOAD MANUAL"}
        </button>
      </div>
    </div>
  );
}

function ResetDemoDataCard() {
  const [emails, setEmails] = useState("owner@bigrigtrans.com");
  const [pattern, setPattern] = useState("test");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const handleReset = async () => {
    const confirmText = window.prompt(
      "DANGER ZONE — this will permanently delete the listed demo users AND any client whose " +
      "email/name/company name contains the pattern, AND all their data (drivers, vehicles, " +
      "inspections, violations, accidents, filings, documents, IFTA, notifications, " +
      "payments, support chats). It also wipes ALL CSA snapshots and orphaned data.\n\n" +
      "ADMIN / SUPER-ADMIN accounts are NEVER deleted, even if they match the pattern.\n\n" +
      'Type exactly:  RESET DEMO DATA  to confirm.'
    );
    if (confirmText !== "RESET DEMO DATA") {
      if (confirmText !== null) toast.error("Confirmation text did not match. Nothing was deleted.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const list = emails.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/admin/reset-demo-data", {
        confirm: "RESET DEMO DATA",
        delete_emails: list,
        delete_pattern: pattern.trim() || null,
        wipe_csa_test_data: true,
        wipe_orphan_data: true,
      });
      setResult(data);
      toast.success(`Wiped — ${data.clients_remaining} clients remain.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="hs-card p-6 border-2 border-rose-200 bg-rose-50/30" data-testid="settings-reset-demo-card">
      <div className="flex items-center gap-2 mb-3">
        <span className="hs-tag hs-tag-danger">DANGER ZONE</span>
        <h2 className="font-display font-bold text-xl">Reset Demo Data</h2>
      </div>
      <p className="text-xs text-slate-600 mb-4 max-w-2xl">
        Use this <strong>once</strong> right after launching to wipe seed/test data and any specific demo
        client accounts. Your <strong>admin / super-admin accounts and app settings are always preserved</strong>.
        Real client accounts NOT listed below (and NOT matching the pattern) are preserved.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="hs-overline mb-1 block">Demo emails to delete (one per line or comma-separated)</label>
          <textarea
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            className="hs-input font-mono text-xs min-h-[80px]"
            placeholder="owner@bigrigtrans.com"
            data-testid="settings-reset-demo-emails"
          />
        </div>
        <div>
          <label className="hs-overline mb-1 block">Wipe pattern (matches email / name / company name)</label>
          <input
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            className="hs-input font-mono text-xs"
            placeholder="test"
            data-testid="settings-reset-demo-pattern"
          />
          <p className="text-[11px] text-slate-500 mt-2">
            Example: <code>test</code> wipes <em>Test Driver</em>, <em>test@example.com</em>,
            <em>TEST Carrier Inc</em>, etc. Case-insensitive substring match. Leave blank to skip.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button onClick={handleReset} disabled={busy}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm uppercase tracking-wide rounded inline-flex items-center gap-2"
                data-testid="settings-reset-demo-btn">
          {busy ? "WIPING…" : "WIPE DEMO DATA"}
        </button>
        <span className="text-[11px] text-slate-500">
          Requires typing &ldquo;RESET DEMO DATA&rdquo; in the confirmation prompt.
        </span>
      </div>
      {result && (
        <pre className="mt-4 hs-card p-3 text-[11px] font-mono whitespace-pre-wrap overflow-x-auto bg-white border border-slate-200">
{JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}
